import { Box, Paper } from "@mui/material"
import { AddOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { ChartOfAccountsTable } from "./components/ChartOfAccountsTable"
import { FilterChips } from "./components/filters/FilterChips"
import { FilterDrawer } from "./components/filters/FilterDrawer"
import { GlAccountFormDialog } from "./components/GlAccountFormDialog/GlAccountFormDialog"
import { CHART_OF_ACCOUNTS_SEARCH_PLACEHOLDER } from "./constants"
import type { ChartOfAccountsSortableAccessorKey } from "./constants/sorting"
import { accessorKeyToSortField, sortFieldToAccessorKey } from "./constants/sorting"
import { useCreateGlAccount } from "./hooks/useCreateGlAccount"
import { useUpdateGlAccount } from "./hooks/useUpdateGlAccount"
import type { GlAccountFormValues } from "./schemas/glAccountForm"
import type { GlAccountSearchFilters } from "./stores/useChartOfAccountsStore"
import { useChartOfAccountsStore } from "./stores/useChartOfAccountsStore"
import type { ChartOfAccountsSearchParams } from "./types/searchParams"
import { serializeGlAccountFormData } from "./utils/serializeGlAccountFormData"

import { Button } from "@/components/common/Button"
import ToggleInactiveButton from "@/components/common/ToggleInactiveButton"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import { PermissionActionType } from "@/constants/permissions"
import type { GlAccount, GlAccountSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { queryClient } from "@/providers/GraphqlRouterProvider"

export const ChartOfAccountsContainer: FC = () => {
  const navigate = useNavigate()
  const currentSearch = useSearch({ from: "/app/admin/accounting/_layout/accounts-chart" })
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [isAccountFormDialogOpen, setIsAccountFormDialogOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<GlAccount | null>(null)

  const data = useChartOfAccountsStore((state) => state.data)
  const fetching = useChartOfAccountsStore((state) => state.fetching)
  const error = useChartOfAccountsStore((state) => state.error)
  const executeSearch = useChartOfAccountsStore((state) => state.executeSearch)
  const loadMore = useChartOfAccountsStore((state) => state.loadMore)
  const clearStore = useChartOfAccountsStore((state) => state.clearStore)

  const { hasPermissions } = usePermissionManager()
  const hasMaintenancePermission = hasPermissions([PermissionActionType.ACCOUNTING_MAINTENANCE])

  const { createGlAccount } = useCreateGlAccount()
  const { updateGlAccount } = useUpdateGlAccount()

  const { field, order, query, classification, accountTypes, reportingAccountType, showInactive } = currentSearch

  const tableContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  useEffect(() => {
    const searchFilters: GlAccountSearchFilters = {
      field,
      order,
      query,
      classification,
      accountTypes,
      reportingAccountType,
      showInactive,
    }
    executeSearch(queryClient, searchFilters)
  }, [field, order, query, classification, accountTypes, reportingAccountType, showInactive, executeSearch])

  const handleLoadMore = useCallback(() => {
    const searchFilters: GlAccountSearchFilters = {
      field,
      order,
      query,
      classification,
      accountTypes,
      reportingAccountType,
      showInactive,
    }
    loadMore(queryClient, searchFilters)
  }, [field, order, query, classification, accountTypes, reportingAccountType, showInactive, loadMore])

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: handleLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = sortFieldToAccessorKey.get(field)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterFn) => {
      // @ts-expect-error - updaterFn is known fn
      const newSorting = updaterFn(sorting)
      if (newSorting.length) {
        const sort = newSorting[0]
        const key = sort.id as ChartOfAccountsSortableAccessorKey
        const sortField = accessorKeyToSortField.get(key) as GlAccountSortField

        navigate({
          search: (prev) => ({
            ...prev,
            field: sortField,
            order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
          }),
          to: "/app/admin/accounting/accounts-chart",
          replace: true,
        })
      }
    },
    [navigate, sorting]
  )

  const handleToggleInactive = useCallback(() => {
    void navigate({
      search: (prev) => ({
        ...prev,
        showInactive: !showInactive,
      }),
      to: "/app/admin/accounting/accounts-chart",
      replace: true,
    })
  }, [showInactive, navigate])

  const handleAccountSubmit = useCallback(
    async (formValues: GlAccountFormValues, glAccountId?: string | null) => {
      const input = serializeGlAccountFormData(formValues, glAccountId)

      if (glAccountId) {
        return await updateGlAccount(input)
      } else {
        return await createGlAccount(input)
      }
    },
    [createGlAccount, updateGlAccount]
  )

  const handleCloseDialog = useCallback(
    (shouldRefetch?: boolean) => {
      setIsAccountFormDialogOpen(false)
      setEditingAccount(null)
      if (shouldRefetch) {
        const searchFilters: GlAccountSearchFilters = {
          field,
          order,
          query,
          classification,
          accountTypes,
          reportingAccountType,
          showInactive,
        }
        executeSearch(queryClient, searchFilters, true)
      }
    },
    [field, order, query, classification, accountTypes, reportingAccountType, showInactive, executeSearch]
  )

  return (
    <Box className='flex size-full flex-col'>
      <Paper elevation={0} className='flex min-h-0 grow flex-col gap-4 overflow-hidden bg-white'>
        <FilterDrawer showFilterDrawer={filterDrawerOpen} toggleDrawer={() => setFilterDrawerOpen((prev) => !prev)} />

        <TableHeaderActions<ChartOfAccountsSearchParams>
          currentSearch={currentSearch}
          placeholder={CHART_OF_ACCOUNTS_SEARCH_PLACEHOLDER}
          navigateTo='/app/admin/accounting/accounts-chart'
        >
          <Button
            appearance='outlined'
            startIcon={<FilterAltOutlined />}
            onClick={() => setFilterDrawerOpen(true)}
            rootElementName='button'
            data-testid='chart-of-accounts.filter-button'
          >
            Filter
          </Button>

          <Box className='flex justify-end gap-2'>
            <ToggleInactiveButton
              showInactive={showInactive}
              onToggle={handleToggleInactive}
              label='Accounts'
              data-testid='chart-of-accounts.toggle-inactive-button'
            />

            {hasMaintenancePermission && (
              <Button
                appearance='contained'
                startIcon={<AddOutlined />}
                onClick={() => setIsAccountFormDialogOpen(true)}
                rootElementName='button'
                data-testid='chart-of-accounts.add-account-button'
              >
                Add Account
              </Button>
            )}
          </Box>
        </TableHeaderActions>

        <FilterChips />

        <Box className='flex flex-1 flex-col overflow-y-auto'>
          <ChartOfAccountsTable
            data={data}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            loading={fetching}
            error={error?.message}
            scrollHandler={handleScroll}
            tableContainerRef={tableContainerRef}
            onEdit={
              hasMaintenancePermission
                ? (account) => {
                    setEditingAccount(account)
                    setIsAccountFormDialogOpen(true)
                  }
                : undefined
            }
          />
        </Box>
      </Paper>

      {isAccountFormDialogOpen && (
        <GlAccountFormDialog onClose={handleCloseDialog} onSubmit={handleAccountSubmit} account={editingAccount} />
      )}
    </Box>
  )
}
